/* istanbul ignore file */
import { useEffect, useState } from 'react';
import { TextField, useFieldHelper } from 'framework/ui/field';
import { InputLoader } from 'framework/ui/mui-builder';
import { InputTooltip } from 'framework/ui/layout';
import { SectionRow } from 'framework/ui/layout';
import useAccountManagementService from '../../useAccountManagementService';

const TOOLTIP_MESSAGE =
  'When this flag is set to true, information related to this account will be preserved for litigation purposes and will not be deleted according to standard retention policies until the flag is set to false.';

type LoadState = { status: 'loading' } | { status: 'hidden' } | { status: 'loaded'; value: boolean; reason: string };

const LitigationHoldRow = () => {
  const { getValue: getAccountId } = useFieldHelper({ source: 'accountId' });
  const accountAlias = getAccountId() as string | undefined;
  const { getLitigationHold } = useAccountManagementService();
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!accountAlias) {
      setLoadState({ status: 'hidden' });
      return;
    }
    let cancelled = false;
    getLitigationHold(accountAlias)
      .then(dto => {
        if (cancelled) return;
        if (dto == null) {
          setLoadState({ status: 'hidden' });
        } else {
          setLoadState({
            status: 'loaded',
            value: dto.isOnLitigationHold ?? false,
            reason: dto.litigationHoldReason ?? '',
          });
        }
      })
      .catch(err => {
        if (!cancelled) throw err;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when account alias changes
  }, [accountAlias]);

  if (loadState.status === 'hidden') {
    return null;
  }

  const label = (
    <>
      Is on litigation hold?
      <InputTooltip message={TOOLTIP_MESSAGE} source="litigationHold" />
    </>
  );

  if (loadState.status === 'loading') {
    return <TextField label={label} value={<InputLoader />} />;
  }

  const holdValue = loadState.value ? 'Yes' : 'No';
  const reason = loadState.reason?.trim();

  return (
    <SectionRow>
      <TextField label={label} value={holdValue} />
      {loadState.value && reason && <TextField label="Litigation hold reason" value={reason} />}
    </SectionRow>
  );
};

export default LitigationHoldRow;
